/**
 * Prisgraf: spotpris (inkl. 25 % mva) per time for i dag og ev. i morgen.
 * Én serie → ingen legend; nå-time markeres. Hover gir eksakt verdi.
 */
import type { SpotPrice } from '../lib/api.ts';

interface Props {
  idag: SpotPrice[];
  imorgen: SpotPrice[];
  area: string;
}

const MVA = 1.25;

function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString('nb-NO', {
    timeZone: 'Europe/Oslo',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function PriceChart({ idag, imorgen, area }: Props) {
  const alle = [
    ...idag.map((p) => ({ ...p, dag: 'I dag' })),
    ...imorgen.map((p) => ({ ...p, dag: 'I morgen' })),
  ];
  if (!alle.length) {
    return (
      <div className="rounded-xl bg-white p-6 text-sm text-slate-500 ring-1 ring-slate-200">
        Ingen prisdata for {area} ennå.
      </div>
    );
  }

  const verdier = alle.map((p) => Number(p.nokPerKwhEksMva) * MVA * 100); // øre inkl. mva
  const maks = Math.max(...verdier, 10);
  const naa = Date.now();

  const W = 900;
  const H = 190;
  const PAD_L = 44;
  const PAD_B = 24;
  const PAD_T = 10;
  const plotW = W - PAD_L - 6;
  const plotH = H - PAD_T - PAD_B;
  const bw = plotW / alle.length;

  const minIdx = verdier.indexOf(Math.min(...verdier));
  const maksIdx = verdier.indexOf(Math.max(...verdier));

  // Y-akse: 3 runde gridlinjer
  const step = Math.ceil(maks / 3 / 10) * 10;
  const gridVerdier = [step, step * 2, step * 3].filter((v) => v <= maks * 1.15);

  const dagSkilleIdx = idag.length; // der «i morgen» begynner

  return (
    <div className="rounded-xl bg-white p-4 ring-1 ring-slate-200">
      <div className="mb-2 flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-slate-700">
          Strømpris {area} <span className="font-normal text-slate-400">(spot inkl. mva, øre/kWh)</span>
        </h3>
        {imorgen.length === 0 && (
          <span className="text-xs text-slate-400">Morgendagens priser publiseres ~13:00</span>
        )}
      </div>
      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${W} ${H}`} className="min-w-[600px]" role="img" aria-label={`Strømpris ${area} per time`}>
          {gridVerdier.map((v) => {
            const y = PAD_T + plotH - (v / (maks * 1.1)) * plotH;
            return (
              <g key={v}>
                <line x1={PAD_L} x2={W - 6} y1={y} y2={y} stroke="#e2e8f0" strokeWidth="1" />
                <text x={PAD_L - 6} y={y + 3} textAnchor="end" fontSize="10" fill="#94a3b8">
                  {v}
                </text>
              </g>
            );
          })}

          {dagSkilleIdx > 0 && dagSkilleIdx < alle.length && (
            <line
              x1={PAD_L + dagSkilleIdx * bw}
              x2={PAD_L + dagSkilleIdx * bw}
              y1={PAD_T}
              y2={PAD_T + plotH}
              stroke="#cbd5e1"
              strokeWidth="1"
              strokeDasharray="4 3"
            />
          )}

          {alle.map((p, i) => {
            const v = verdier[i];
            const start = new Date(p.ts).getTime();
            const slutt = start + p.varighetMin * 60_000;
            const erNaa = naa >= start && naa < slutt;
            const h = Math.max((v / (maks * 1.1)) * plotH, 1.5);
            const x = PAD_L + i * bw;
            const y = PAD_T + plotH - h;
            return (
              <g key={p.ts}>
                <rect
                  x={x + 1}
                  y={y}
                  width={Math.max(bw - 2, 1.5)}
                  height={h}
                  rx="2"
                  fill={erNaa ? '#1d4ed8' : i >= dagSkilleIdx ? '#93c5fd' : '#3b82f6'}
                >
                  <title>{`${p.dag} ${timeLabel(p.ts)} — ${v.toFixed(1)} øre/kWh`}</title>
                </rect>
                {(i === minIdx || i === maksIdx) && (
                  <text
                    x={x + bw / 2}
                    y={y - 4}
                    textAnchor="middle"
                    fontSize="10"
                    fontWeight="600"
                    fill="#475569"
                  >
                    {Math.round(v)}
                  </text>
                )}
              </g>
            );
          })}

          {/* Tidsakse: hver 6. time */}
          {alle.map((p, i) =>
            i % 6 === 0 ? (
              <text
                key={`t-${p.ts}`}
                x={PAD_L + i * bw + bw / 2}
                y={H - 8}
                textAnchor="middle"
                fontSize="10"
                fill="#94a3b8"
              >
                {timeLabel(p.ts)}
              </text>
            ) : null,
          )}
        </svg>
      </div>
      <div className="mt-1 flex gap-4 text-xs text-slate-400">
        <span>
          <span className="mr-1 inline-block h-2 w-2 rounded-sm bg-blue-500" />I dag
        </span>
        <span>
          <span className="mr-1 inline-block h-2 w-2 rounded-sm bg-blue-300" />I morgen
        </span>
        <span>
          <span className="mr-1 inline-block h-2 w-2 rounded-sm bg-blue-700" />Nå
        </span>
      </div>
    </div>
  );
}
